import { getApiErrorDetail, useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { HandlingUnitType, UpdateHandlingUnitType } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { createHandlingUnitTypeApi, handlingUnitTypeKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UpdateHandlingUnitTypeVariables {
  data: UpdateHandlingUnitType;
  id: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateHandlingUnitType(): UseMutationResult<
  HandlingUnitType,
  Error,
  UpdateHandlingUnitTypeVariables
> {
  const client = useApiClient('admin-auth');
  const api = createHandlingUnitTypeApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<HandlingUnitType, Error, UpdateHandlingUnitTypeVariables>({
    mutationFn: ({ data, id }: UpdateHandlingUnitTypeVariables) => api.updateHandlingUnitType(id, data),
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      if (detail.errorCode === 'Conflict.DuplicateHandlingUnitTypeLabel') {
        toast.error(t('handlingUnitTypes.toast.errorDuplicateLabel'));
      } else {
        toast.error(detail.message || t('handlingUnitTypes.toast.updateError'));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: handlingUnitTypeKeys.lists() });
      toast.success(t('handlingUnitTypes.toast.updated'));
    },
  });
}
